import { AnimatePresence } from 'framer-motion';
import ReactMarkdown from 'react-markdown';
import { Link, useNavigate, useRoutes } from 'react-router-dom';
import styled from 'styled-components';
import { Accordion } from '../components/accordion/accordion';
import { AccordionItem } from '../components/accordion/accordion-item';
import { BidSheet } from '../components/bid-sheet/bid-sheet';
import { Breadcrumbs } from '../components/breadcrumbs/breadcrumbs';
import { ArrowButton } from '../components/button/arrow-button';
import { Button } from '../components/button/button';
import { Container } from '../components/container/container';
import { LotDetails } from '../components/lot/lot-details';
import { LotNav } from '../components/nav/lot-nav';
import { SrOnly } from '../components/sr-only/sr-only';
import ChevronSvg from '../components/svg/chevron';
import { LotDetailsContainer } from '../containers/lot-details-container';
import type { LotQuery, MeQuery } from '../graphql/types';
import { useLotTimer } from '../utils/use-lot-timer';

const Flex = styled.span`
  display: flex;
`;

export function LotDetail({
  lot,
  loading,
  auction,
}: {
  lot?: LotQuery['Lot'];
  loading: boolean;
  auction?: MeQuery['activeAuction'];
}) {
  const routes = useRoutes([
    {
      path: '/bid',
      element: <BidSheet lot={lot} />,
    },
  ]);
  const navigate = useNavigate();
  const isLoading = loading || !lot;
  const year = lot?.startsAt?.substring(0, 4) ?? '';
  const auctionUrl = year.length === 4 ? `/${year}/lots` : '/lots';

  const { isEnded } = useLotTimer({
    startsAt: lot?.startsAt,
    endsAt: lot?.endsAt,
  });

  return (
    <>
      <Container>
        <LotNav
          left={
            <Breadcrumbs>
              <Link to={auctionUrl}>Auction</Link>
              <Flex>Lot {lot && lot.number}</Flex>
            </Breadcrumbs>
          }
          right={
            <>
              <ArrowButton
                variant="tertiary"
                onClick={() => navigate(`/lot/${lot?.previousLotId}`)}
                disabled={!lot?.previousLotId}
              >
                <ChevronSvg isLeft={true} />
                <SrOnly>Previous Lot</SrOnly>
              </ArrowButton>
              <ArrowButton
                variant="tertiary"
                onClick={() => navigate(`/lot/${lot?.nextLotId}`)}
                disabled={!lot?.nextLotId}
              >
                <ChevronSvg />
                <SrOnly>Next Lot</SrOnly>
              </ArrowButton>
            </>
          }
        />
        {isLoading ? (
          <LotDetails
            isLoading={true}
            brandName=""
            title=""
            summary=""
            highestBid={9}
            isLotClosed={isEnded}
            buyersPremiumPercentage={auction?.buyersPremiumPercentage}
            overheadPremiumPercentage={auction?.overheadPremiumPercentage}
          />
        ) : (
          <>
            <LotDetailsContainer
              lot={lot as any}
              actions={
                <Button
                  as={Link}
                  to={`${window.location.pathname}/leaderboard`}
                  variant="secondary"
                  fullWidth
                >
                  Go to leaderboard
                </Button>
              }
              isLotClosed={isEnded}
              buyersPremiumPercentage={auction?.buyersPremiumPercentage}
              overheadPremiumPercentage={auction?.overheadPremiumPercentage}
            />
            <Accordion>
              <AccordionItem title="Details">
                <h2>Description</h2>
                <ReactMarkdown>{lot?.description || ''}</ReactMarkdown>
                <br />
                <h2>Color</h2>
                <p>{lot?.lotType}</p>
                <h2>Grape</h2>
                <p>{lot?.variety}</p>
                <h2>Vintage</h2>
                <p>{lot?.vintage ?? 'N/A'}</p>
                <h2>Region</h2>
                <p>{lot?.region}</p>
                <h2>Producer</h2>
                <p>{lot?.vintner}</p>
              </AccordionItem>
            </Accordion>
          </>
        )}
      </Container>
      <AnimatePresence exitBeforeEnter>{routes}</AnimatePresence>
    </>
  );
}
